/**
 * **A client's clip on a task card, in its own shape** — the poster, the play mark and the length.
 *
 * Moved out of `FeedbackClipRow` on 24 September 2026, when the task cards drew the clip at its
 * natural proportion (owner: 16:9 for a landscape film, "never a stretched strip") with the question
 * or the set beside it.
 *
 * ── The card never plays the film ────────────────────────────────────────────────────────────
 * There is ONE player (view ADR of 23 September 2026, `features/clip-player`), on the client's card
 * at `?tab=forma&clip=`. It marks the clip seen on its first presented frame, which is the implicit
 * resolution `exercise-clip-timeline` R9/R18 name, and it carries the discussion R15 now lets this
 * panel write in. The frame is a mouse shortcut to the card's own primary, which leads to the same
 * place; it takes no tab stop, because the primary already is one.
 *
 * ── The box is sized from the wire before the poster arrives ─────────────────────────────────
 * `clipWidth` / `clipHeight` give the proportion, so nothing moves when the poster lands, and a clip
 * with no poster draws the same box with the play mark on the ground. The poster is read only once
 * the frame has been on screen (`useOnScreen`).
 */

import { useRef, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useOnScreen } from '~/shared/lib/useOnScreen';
import photo from '~/shared/ui/photoBox.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { TaskDensity } from '../lib/taskWrites';
import { useClipMedia } from '../lib/useClipMedia';
import { PlayMark } from './HomeGlyphs';
import styles from './clipFrame.module.css';

interface ClipFrameProps {
  readonly clipId: number;
  /** `width / height` — `entities/exercise-clip`'s `clipAspect` over the queue payload. */
  readonly aspect: number;
  /** "0:42", or `''` where the film was never measured. */
  readonly duration: string;
  readonly density: TaskDensity;
  /** Open the one player — the card's primary's destination. `null` where there is no clip to open. */
  readonly onOpen: (() => void) | null;
}

export function ClipFrame({ clipId, aspect, duration, density, onOpen }: ClipFrameProps) {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLDivElement>(null);
  const media = useClipMedia(clipId, useOnScreen(frameRef));

  return (
    <>
      <div
        aria-hidden="true"
        className={`${String(styles.frame)} ${String(styles[density])} ${onOpen === null ? '' : String(styles.target)}`}
        ref={frameRef}
        style={{ '--clip-aspect': String(aspect) } as CSSProperties}
        onClick={onOpen ?? undefined}
      >
        {media.posterUrl === null ? null : (
          <img
            className={`${String(photo.photo)} ${String(styles.poster)}`}
            src={media.posterUrl}
            alt=""
            decoding="async"
          />
        )}
        <span className={styles.playMark}>
          <PlayMark className={styles.playMarkGlyph} />
        </span>
        {duration === '' ? null : <span className={styles.duration}>{duration}</span>}
      </div>
      {media.status === 'ready' && media.posterUrl === null ? (
        <span className={hidden.srOnly}>{t('web:home.feedback.clip.noPoster')}</span>
      ) : null}
    </>
  );
}
